import React from 'react';

interface NotionBlock {
  id: string;
  type: string;
  [key: string]: any;
  children?: NotionBlock[];
}

interface NotionBlockRendererProps {
  blocks: NotionBlock[];
}

export function NotionBlockRenderer({ blocks }: NotionBlockRendererProps) {
  if (!blocks || blocks.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {blocks.map((block) => (
        <NotionBlockComponent key={block.id} block={block} />
      ))}
    </div>
  );
}

function NotionBlockComponent({ block }: { block: NotionBlock }) {
  const { type } = block;
  const blockContent = block[type];

  switch (type) {
    case 'paragraph':
      return (
        <p className="text-sm text-foreground whitespace-pre-wrap">
          {renderRichText(blockContent?.rich_text || [])}
        </p>
      );

    case 'heading_1':
      return (
        <h1 className="text-2xl font-bold text-foreground mt-4 mb-2">
          {renderRichText(blockContent?.rich_text || [])}
        </h1>
      );

    case 'heading_2':
      return (
        <h2 className="text-xl font-bold text-foreground mt-3 mb-2">
          {renderRichText(blockContent?.rich_text || [])}
        </h2>
      );

    case 'heading_3':
      return (
        <h3 className="text-lg font-semibold text-foreground mt-2 mb-1">
          {renderRichText(blockContent?.rich_text || [])}
        </h3>
      );

    case 'bulleted_list_item':
      return (
        <div className="flex items-start gap-2">
          <span className="text-foreground mt-1">•</span>
          <div className="flex-1">
            <p className="text-sm text-foreground">
              {renderRichText(blockContent?.rich_text || [])}
            </p>
            {block.children && block.children.length > 0 && (
              <div className="ml-4 mt-1">
                <NotionBlockRenderer blocks={block.children} />
              </div>
            )}
          </div>
        </div>
      );

    case 'numbered_list_item':
      return (
        <div className="flex items-start gap-2">
          <span className="text-foreground mt-1">1.</span>
          <div className="flex-1">
            <p className="text-sm text-foreground">
              {renderRichText(blockContent?.rich_text || [])}
            </p>
            {block.children && block.children.length > 0 && (
              <div className="ml-4 mt-1">
                <NotionBlockRenderer blocks={block.children} />
              </div>
            )}
          </div>
        </div>
      );

    case 'to_do':
      return (
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={blockContent?.checked || false}
            readOnly
            className="mt-1"
          />
          <p
            className={`text-sm flex-1 ${
              blockContent?.checked ? 'line-through text-muted-foreground' : 'text-foreground'
            }`}
          >
            {renderRichText(blockContent?.rich_text || [])}
          </p>
        </div>
      );

    case 'toggle':
      return (
        <details className="mb-2">
          <summary className="cursor-pointer text-sm font-medium text-foreground">
            {renderRichText(blockContent?.rich_text || [])}
          </summary>
          {block.children && block.children.length > 0 && (
            <div className="ml-4 mt-2">
              <NotionBlockRenderer blocks={block.children} />
            </div>
          )}
        </details>
      );

    case 'quote':
      return (
        <blockquote className="border-l-4 border-primary/50 pl-4 italic text-sm text-muted-foreground">
          {renderRichText(blockContent?.rich_text || [])}
        </blockquote>
      );

    case 'code':
      return (
        <pre className="bg-muted p-3 rounded-md overflow-x-auto text-xs">
          <code className="text-foreground">
            {blockContent?.rich_text
              ?.map((text: any) => text.plain_text)
              .join('') || ''}
          </code>
        </pre>
      );

    case 'image': {
      const imageUrl =
        blockContent?.type === 'external'
          ? blockContent.external?.url
          : blockContent?.file?.url;
      const caption = blockContent?.caption
        ?.map((text: any) => text.plain_text)
        .join('') || '';
      return (
        <div className="my-2">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={caption}
              className="max-w-full rounded-md"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          {caption && (
            <p className="text-xs text-muted-foreground mt-1 text-center">{caption}</p>
          )}
        </div>
      );
    }

    case 'divider':
      return <hr className="my-4 border-border" />;

    case 'callout': {
      const icon = blockContent?.icon;
      return (
        <div className="bg-muted/50 border-l-4 border-primary p-3 rounded-md my-2">
          <div className="flex items-start gap-2">
            {icon && <span className="text-lg">{getIconEmoji(icon)}</span>}
            <div className="flex-1 text-sm text-foreground">
              {renderRichText(blockContent?.rich_text || [])}
            </div>
          </div>
        </div>
      );
    }

    case 'table':
      return (
        <div className="overflow-x-auto my-2">
          <table className="min-w-full border border-border rounded-md">
            <tbody>
              {blockContent?.table_rows?.map((row: any, rowIndex: number) => (
                <tr key={rowIndex} className="border-b border-border">
                  {row.cells?.map((cell: any, cellIndex: number) => (
                    <td
                      key={cellIndex}
                      className="px-3 py-2 text-sm text-foreground border-r border-border last:border-r-0"
                    >
                      {renderRichText(cell || [])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    default:
      // Para tipos não suportados, tentar renderizar rich_text se existir
      if (blockContent?.rich_text) {
        return (
          <p className="text-sm text-muted-foreground">
            {renderRichText(blockContent.rich_text)}
          </p>
        );
      }
      return null;
  }
}

function renderRichText(richText: any[]): React.ReactNode {
  if (!richText || richText.length === 0) {
    return null;
  }

  return richText.map((text, index) => {
    const { plain_text, annotations, href } = text;
    const { bold, italic, strikethrough, underline, code, color } = annotations || {};

    let className = '';
    if (bold) className += 'font-bold ';
    if (italic) className += 'italic ';
    if (strikethrough) className += 'line-through ';
    if (underline) className += 'underline ';
    if (code) className += 'bg-muted px-1 rounded text-xs font-mono ';

    const style = color && color !== 'default' ? { color: getColorValue(color) } : {};

    const content = (
      <span key={index} className={className.trim() || undefined} style={style}>
        {plain_text}
      </span>
    );

    if (href) {
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {content}
        </a>
      );
    }

    return content;
  });
}

function getIconEmoji(icon: any): string {
  if (icon?.type === 'emoji') {
    return icon.emoji;
  }
  return '💡';
}

function getColorValue(color: string): string {
  const colorMap: Record<string, string> = {
    gray: '#6B7280',
    brown: '#92400E',
    orange: '#EA580C',
    yellow: '#F59E0B',
    green: '#10B981',
    blue: '#3B82F6',
    purple: '#8B5CF6',
    pink: '#EC4899',
    red: '#EF4444',
  };
  return colorMap[color] || color;
}

